import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

function CrearActividad() {
    const { sistemaId } = useParams();
    const navigate = useNavigate();
    const { token, user: currentUser } = useAuth();

    // --- Estados del formulario ---
    const [feedback, setFeedback] = useState({ type: '', message: '', details: [] });
    const [titulo, setTitulo] = useState('');
    const [descripcion, setDescripcion] = useState('');
    const [fechaVencimiento, setFechaVencimiento] = useState('');
    const [usuariosAsignados, setUsuariosAsignados] = useState([]);
    const [agenciasSeleccionadas, setAgenciasSeleccionadas] = useState([]);
    const [filtroAgencia, setFiltroAgencia] = useState('');
    const [incluirSubtareas, setIncluirSubtareas] = useState(false);
    const [subtareas, setSubtareas] = useState(['']);
    const [esRecurrente, setEsRecurrente] = useState(false);
    const [frecuencia, setFrecuencia] = useState('semanal');
    const [intervalo, setIntervalo] = useState(1);
    const [diasSemana, setDiasSemana] = useState([]);
    const [fechaFinal, setFechaFinal] = useState('');
    
    const [trabajoId, setTrabajoId] = useState('');

    // --- Estados para los datos de los selectores ---
    const [listaUsuarios, setListaUsuarios] = useState([]);
    const [listaAgencias, setListaAgencias] = useState([]);
    const [listaTrabajos, setListaTrabajos] = useState([]);
    const [loading, setLoading] = useState(true);

    // Asigna la tarea al usuario actual si no es admin
    useEffect(() => {
        if (currentUser && currentUser.rol !== 'admin') {
            setUsuariosAsignados([currentUser.id]);
        }
    }, [currentUser]);

    // Carga los datos necesarios para los menús desplegables del formulario
    useEffect(() => {
        const cargarDatosIniciales = async () => {
            if (!token) return;
            setLoading(true);
            try {
                const headers = { 'Authorization': `Bearer ${token}` };
                // --- MODIFICADO ---
               const [usuariosRes, agenciasRes, trabajosRes] = await Promise.all([
                    fetch(`/api/sistemas/${sistemaId}/usuarios`, { headers }),
                    fetch('/api/agencias-accesibles', { headers }), // <-- LÍNEA CORREGIDA
                    fetch('/api/trabajos', { headers })
                ]);
                
                if (!usuariosRes.ok || !agenciasRes.ok || !trabajosRes.ok) {
                    throw new Error('Error al cargar los datos necesarios para el formulario.');
                }
                
                const usuariosData = await usuariosRes.json();
                const agenciasData = await agenciasRes.json();
                const trabajosData = await trabajosRes.json();

                setListaUsuarios(usuariosData);
                setListaAgencias(agenciasData);
                setListaTrabajos(trabajosData);

            } catch (error) {
                setFeedback({ type: 'error', message: error.message });
            } finally {
                setLoading(false);
            }
        };
        cargarDatosIniciales();
    }, [token, sistemaId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFeedback({ type: '', message: '' }); // Limpia feedback previo

        if (agenciasSeleccionadas.length === 0) {
            setFeedback({ type: 'error', message: 'Debe seleccionar al menos una agencia.' });
            return;
        }
        if (usuariosAsignados.length === 0) {
            setFeedback({ type: 'error', message: 'Debe asignar la tarea al menos a un usuario.' });
            return;
        }

        const body = {
            titulo,
            descripcion,
            fecha_vencimiento: fechaVencimiento,
            trabajo_id: trabajoId,
            agencias_ids: agenciasSeleccionadas,
            usuariosAsignados,
            subtareas: incluirSubtareas ? subtareas.filter(st => st.trim() !== '') : [],
            es_recurrente: esRecurrente,
            recurrencia_frecuencia: esRecurrente ? frecuencia : null,
            recurrencia_intervalo: esRecurrente ? intervalo : null,
            recurrencia_dias_semana: esRecurrente && frecuencia === 'semanal' ? diasSemana : [],
            recurrencia_fecha_final: esRecurrente ? fechaFinal || null : null,
        };

        try {
            const response = await fetch(`/api/actsis/${sistemaId}/tareas`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMsg = data.errors ? data.errors.map(e => e.msg).join(', ') : (data.message || 'Error al crear la tarea.');
                setFeedback({ type: 'error', message: errorMsg });
            } else {
                // Si hay usuarios omitidos, el mensaje será de tipo 'warning'
                const feedbackType = data.details && data.details.omitidas > 0 ? 'warning' : 'success';
                setFeedback({ type: feedbackType, message: data.message });
                setTimeout(() => navigate(`/sistemas/${sistemaId}`), 3000); // Dar tiempo para leer el feedback
            }

        } catch (err) {
            setFeedback({ type: 'error', message: 'Error de red. No se pudo conectar con el servidor.' });
        }
    };
    
    // Handlers para los campos del formulario
    const handleSubtareaChange = (index, value) => {
        const nuevasSubtareas = [...subtareas];
        nuevasSubtareas[index] = value;
        setSubtareas(nuevasSubtareas);
    };

    const agregarSubtarea = () => setSubtareas([...subtareas, '']);
    const quitarSubtarea = (index) => setSubtareas(subtareas.filter((_, i) => i !== index));
    const handleUserAssign = (userId) => {
        setUsuariosAsignados(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };
    const handleAgenciaSelect = (agenciaId) => {
        setAgenciasSeleccionadas(prev => prev.includes(agenciaId) ? prev.filter(id => id !== agenciaId) : [...prev, agenciaId]);
    };
    const handleDiaSemanaChange = (dia) => {
        setDiasSemana(prev => prev.includes(dia) ? prev.filter(d => d !== dia) : [...prev, dia]);
    };
    
    if (loading) {
        return <div className="text-center mt-5"><div className="spinner-border" role="status"><span className="visually-hidden">Cargando...</span></div></div>;
    }

    const agenciasFiltradas = listaAgencias.filter(agencia => 
        agencia.nombre.toLowerCase().includes(filtroAgencia.toLowerCase())
    );

    return (
        <div className="container mt-4 mb-5">
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2>Crear Nueva Actividad</h2>
                        <Link to={`/sistemas/${sistemaId}`} className="btn btn-secondary">Cancelar</Link>
                    </div>

                    {feedback.message && (
                        <div className={`alert ${feedback.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                            <p className="mb-1">{feedback.message}</p>
                            {feedback.details && feedback.details.length > 0 && (
                                <ul className="mb-0">
                                    {feedback.details.map((detail, index) => <li key={index}><small>{detail}</small></li>)}
                                </ul>
                            )}
                        </div>
                    )}
                    
                    <form onSubmit={handleSubmit} className="card shadow-sm p-4">
                        
                        <div className="mb-3">
                            <label htmlFor="titulo" className="form-label">Título de la Actividad</label>
                            <input type="text" id="titulo" className="form-control" value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
                        </div>
                        
                        <div className="mb-3">
                            <label htmlFor="descripcion" className="form-label">Descripción (Opcional)</label>
                            <textarea id="descripcion" className="form-control" rows="3" value={descripcion} onChange={(e) => setDescripcion(e.target.value)}></textarea>
                        </div>

                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label htmlFor="trabajo" className="form-label">Tipo de Trabajo</label>
                                <select id="trabajo" className="form-select" value={trabajoId} onChange={e => setTrabajoId(e.target.value)} required>
                                    <option value="">Selecciona un trabajo...</option>
                                    {listaTrabajos.map(trabajo => <option key={trabajo.id} value={trabajo.id}>{trabajo.nombre}</option>)}
                                </select>
                            </div>
                            <div className="col-md-6 mb-3">
                                <label htmlFor="fechaVencimiento" className="form-label">Fecha de Vencimiento</label>
                                <input type="date" id="fechaVencimiento" className="form-control" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} required />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Agencias Involucradas</label>
                            <input type="text" className="form-control mb-2" placeholder="Buscar agencia..." value={filtroAgencia} onChange={e => setFiltroAgencia(e.target.value)} />
                            <div className="border rounded p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                {agenciasFiltradas.map(agencia => (
                                    <div className="form-check" key={agencia.id}>
                                        <input className="form-check-input" type="checkbox" id={`agencia-${agencia.id}`} checked={agenciasSeleccionadas.includes(agencia.id)} onChange={() => handleAgenciaSelect(agencia.id)} />
                                        <label className="form-check-label" htmlFor={`agencia-${agencia.id}`}>{agencia.nombre}</label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-check mb-3">
                            <input className="form-check-input" type="checkbox" id="incluirSubtareas" checked={incluirSubtareas} onChange={(e) => setIncluirSubtareas(e.target.checked)} />
                            <label className="form-check-label" htmlFor="incluirSubtareas">Añadir Subtareas</label>
                        </div>
                        {incluirSubtareas && (
                            <div className="mb-3 ps-4">
                                {subtareas.map((subtarea, index) => (
                                    <div className="input-group mb-2" key={index}>
                                        <input type="text" className="form-control" placeholder={`Subtarea ${index + 1}`} value={subtarea} onChange={(e) => handleSubtareaChange(index, e.target.value)} />
                                        <button className="btn btn-outline-danger" type="button" onClick={() => quitarSubtarea(index)}>–</button>
                                    </div>
                                ))}
                                <button className="btn btn-outline-primary btn-sm" type="button" onClick={agregarSubtarea}>+ Agregar Subtarea</button>
                            </div>
                        )}

                        <div className="form-check form-switch mb-3">
                            <input className="form-check-input" type="checkbox" role="switch" id="esRecurrente" checked={esRecurrente} onChange={(e) => setEsRecurrente(e.target.checked)} />
                            <label className="form-check-label" htmlFor="esRecurrente">¿Es una tarea recurrente?</label>
                        </div>
                        {esRecurrente && (
                            <div className="border p-3 rounded mb-3">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="frecuencia" className="form-label">Frecuencia</label>
                                        <select id="frecuencia" className="form-select" value={frecuencia} onChange={e => setFrecuencia(e.target.value)}>
                                            <option value="diaria">Diaria</option>
                                            <option value="semanal">Semanal</option>
                                            <option value="mensual">Mensual</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="intervalo" className="form-label">Cada</label>
                                        <div className="input-group">
                                            <input type="number" id="intervalo" className="form-control" value={intervalo} onChange={e => setIntervalo(parseInt(e.target.value, 10))} min="1" />
                                            <span className="input-group-text">{frecuencia === 'semanal' ? 'semana(s)' : frecuencia === 'mensual' ? 'mes(es)' : 'día(s)'}</span>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label htmlFor="fechaFinal" className="form-label">Finaliza el (Opcional)</label>
                                        <input type="date" id="fechaFinal" className="form-control" value={fechaFinal} onChange={(e) => setFechaFinal(e.target.value)} />
                                    </div>
                                </div>
                                {frecuencia === 'semanal' && (
                                    <div className="mt-3">
                                        <label className="form-label">Repetir los días:</label>
                                        <div>
                                            {['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'].map((dia, index) => (
                                                <div className="form-check form-check-inline" key={index}>
                                                    <input className="form-check-input" type="checkbox" id={`dia-${index}`} checked={diasSemana.includes(index + 1)} onChange={() => handleDiaSemanaChange(index + 1)} />
                                                    <label className="form-check-label" htmlFor={`dia-${index}`}>{dia}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Asignar a:</label>
                            {currentUser.rol === 'admin' ? (
                                <div className="border rounded p-2" style={{maxHeight: '150px', overflowY: 'auto'}}>
                                    {listaUsuarios.map(user => (
                                        <div className="form-check" key={user.id}>
                                            <input className="form-check-input" type="checkbox" value={user.id} id={`user-${user.id}`} checked={usuariosAsignados.includes(user.id)} onChange={() => handleUserAssign(user.id)} />
                                            <label className="form-check-label" htmlFor={`user-${user.id}`}>{user.nombre}</label>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="form-control-plaintext"><strong>{currentUser.nombre}</strong> (A ti mismo)</p>
                            )}
                        </div>
                        
                        <div className="text-end">
                            <button type="submit" className="btn btn-primary">Crear Actividad</button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
}

export default CrearActividad;